import {
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { getScrollContainer } from './utils/scroll-memory';

/**
 * Casts a shadow under a band pinned to the top of the page, but only while it has something to
 * cast it on: at rest the band is part of the page and a shadow would only make it look detached;
 * pinned over the content scrolling past, the shadow is what says the two are not on the same
 * plane.
 *
 * Meant for a band sitting at the very top of the scrolling container, where being scrolled at all
 * and being pinned are the same thing.
 */
@Directive({
  selector: '[appStickyShadow]',
  standalone: true,
})
export class StickyShadowDirective implements OnInit, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);

  private container: HTMLElement | null = null;
  private readonly onScroll = () => {
    this.host.nativeElement.classList.toggle(
      'is-stuck',
      (this.container?.scrollTop ?? 0) > 0
    );
  };

  ngOnInit(): void {
    this.container = getScrollContainer();
    if (!this.container) {
      return;
    }
    // Outside Angular: this fires on every scroll frame and only ever touches a class name.
    this.zone.runOutsideAngular(() => {
      this.container?.addEventListener('scroll', this.onScroll, {
        passive: true,
      });
    });
    // The container may already be scrolled when the page opens — a record reached from a chart
    // scrolls straight to one of its sections.
    this.onScroll();
  }

  ngOnDestroy(): void {
    this.container?.removeEventListener('scroll', this.onScroll);
  }
}
